'use client';

import React, { useEffect, useState } from 'react';
import flightfront from '@/assets/flyghtBody/flight front.svg';
import flyghtback from '@/assets/flyghtBody/flight back.svg';
import wingTop from '@/assets/flyghtBody/wing top.svg';
import wingBottom from '@/assets/flyghtBody/wing bottom.svg';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import Image from '@/lib/app-image';
import { useDispatch, useSelector } from 'react-redux';
import { updateSelectedSSR } from '@flysync/shared-redux/slices/flightSearchSlice';
import { Check } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

function Seat({
  seatMap,
  activeIndexes,
  handleSeat,
}: {
  seatMap: any;
  activeIndexes: any;
  handleSeat: any;
}) {
  const [seatGrid, setSeatGrid] = useState<any[]>([]);
  const [perSideSeats, setPerSideSeats] = useState<number>(0);
  const [activeStatus, setActiveStatus] = useState<string[]>([]);
  const [ssrInfo, setSsrInfo] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  useEffect(() => {
    const seatDataObject = seatMap;
    const { row: numberOfRows, column: numberOfColumns } =
      seatDataObject?.sData || {};
    const rawSInfo = seatDataObject?.sInfo;
    const seatInfo = Array.isArray(rawSInfo)
      ? rawSInfo
      : Object.values(rawSInfo || {});
    const perSideSeat = Math.floor(Number(numberOfColumns) / 2);
    setPerSideSeats(perSideSeat);
    const seatData = Array.from({ length: numberOfRows }, (_, rowIndex) => {
      return Array.from({ length: numberOfColumns + 1 }, (_, colIndex) => {
        if (colIndex === perSideSeat) {
          return null; // Walking space
        }
        const actualColumn = colIndex > perSideSeat ? colIndex : colIndex + 1;
        return seatInfo.find(
          (seat: { seatPosition: { row: any; column: any; }; }) =>
            Number(seat.seatPosition.row) === rowIndex + 1 &&
            Number(seat.seatPosition.column) ===
              numberOfColumns - actualColumn + 1,
        );
      });
    });

    setSeatGrid(seatData);
    setIsLoading(false);
  }, [seatMap]);

  const selectedSSR = useSelector(
    (state: any) => state.flightSearch.selectedSSR,
  );

  useEffect(() => {
    if (selectedSSR.flatMap((item: any) => item).length > 0) {
      const activeStatusData = selectedSSR.flatMap(
        (item: any) => item?.[activeIndexes.i]?.[activeIndexes.index]?.['seat'],
      );
      const activeStatusDataSeatNo = activeStatusData.map(
        (item: any) => item?.seatNo,
      );
      setActiveStatus(activeStatusDataSeatNo);
    }
  }, [selectedSSR, activeIndexes]);

  useEffect(() => {
    const fetchedSsrInfo = [
      { amount: 0, label: `Free`, color: 'bg-gray-200' },
      { amount: 96, label: `₹ 1 - 100`, color: 'bg-teal-300' },
      { amount: 128, label: `₹ 101 - 1000`, color: 'bg-violet-400' },
      { amount: 1275, label: `₹ Above 1001`, color: 'bg-rose-300' },
    ];

    setSsrInfo(fetchedSsrInfo);

    setIsLoading(true);

    if (
      seatGrid?.length == 0 &&
      isLoading &&
      JSON.parse(sessionStorage?.getItem('seatData') || '[]')?.length > 0 &&
      JSON.parse(sessionStorage?.getItem('ssrInfoData') || '[]')?.length > 0
    ) {
      handleSeat();
      setIsLoading(false);
    }
  }, []);

  const freeSeats = ssrInfo.filter((info) => info.amount === 0);
  const lowCostSeats = ssrInfo.filter(
    (info) => info.amount > 0 && info.amount <= 100,
  );
  const midRangeSeats = ssrInfo.filter(
    (info) => info.amount > 100 && info.amount <= 1000,
  );
  const highCostSeats = ssrInfo.filter((info) => info.amount > 1000);
  
  return !isLoading && seatGrid?.length === 0 ? (
    <div className="flex h-80 h-full items-center justify-center">
      <p className="text-sm text-gray-500">No data found</p>
    </div>
  ) : isLoading ? (
    <div className="flex h-80 h-full items-center justify-center">
      <p className="text-sm text-gray-500">Loading...</p>
    </div>
  ) : (
    <div>
      {/* Baggage count */}
      <div className="border-light flex flex-wrap items-center justify-between border-b py-3 md:py-5">
        {/* <p className="text-sm text-gray-500">1 of 1 Seat selected</p> */}

        <div className="flex flex-wrap gap-2 md:gap-3">
          {freeSeats.map((info, index) => (
            <div key={index} className="flex items-center">
              <span
                className={`h-3 w-3 rounded-sm md:h-4 md:w-4 ${info.color}`}
              ></span>
              <p className="ml-1 text-[10px] md:text-xs">{info.label}</p>
            </div>
          ))}
          {lowCostSeats.map((info, index) => (
            <div key={index} className="flex items-center">
              <span
                className={`h-3 w-3 rounded-sm md:h-4 md:w-4 ${info.color}`}
              ></span>
              <p className="ml-1 text-[10px] md:text-xs">{info.label}</p>
            </div>
          ))}
          {midRangeSeats.map((info, index) => (
            <div key={index} className="flex items-center">
              <span
                className={`h-3 w-3 rounded-sm md:h-4 md:w-4 ${info.color}`}
              ></span>
              <p className="ml-1 text-[10px] md:text-xs">{info.label}</p>
            </div>
          ))}
          {highCostSeats.map((info, index) => (
            <div key={index} className="flex items-center">
              <span
                className={`h-3 w-3 rounded-sm md:h-4 md:w-4 ${info.color}`}
              ></span>
              <p className="ml-1 text-[10px] md:text-xs">{info.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Seat Details */}
      <div className="p-2 md:p-5">
        <div className="overflow-x-auto rounded-xl bg-background p-3 md:p-8">
          <div className="flex w-max flex-row items-center md:flex-row">
            {seatGrid && seatGrid?.length && (
              <Image
                src={flightfront}
                width={197}
                height={200}
                alt="Flight front"
                className="h-auto w-[160px] object-contain md:mb-0 md:w-[180px] lg:w-[197px]"
              />
            )}
            <div className="flex h-full w-full flex-col items-center bg-cover bg-center md:w-max">
              {seatGrid && seatGrid?.length && (
                <Image
                  src={wingTop}
                  width={197}
                  height={200}
                  alt="Wing top"
                  className="h-auto w-[120px] object-contain md:w-[150px] lg:w-[197px]"
                />
              )}

              <div className="flex flex-row overflow-x-auto bg-white py-3 md:py-6">
                {seatGrid && seatGrid?.length && (
                  <div className="-my-3 flex flex-col justify-between text-[10px] text-gray-500 md:-my-5 md:text-xs">
                    <span>EXIT</span>
                    <span>EXIT</span>
                  </div>
                )}
                {/* Rendering seat grid */}
                <div className="flex overflow-x-auto">
                  {seatGrid.map((row: any[], rowIndex: number) => (
                    <div key={rowIndex}>
                      {/* Seats in the row */}
                      {row.map((seat: any, colIndex: number) => {
                        if (seat?.seatNo === '') return null;
                        return (
                          <div key={colIndex}>
                            <div className="m-1 rounded text-[10px] md:m-2 md:text-xs">
                              {seat == null && colIndex === perSideSeats && (
                                <div className="p-1 md:p-2">{rowIndex + 1}</div>
                              )}
                              {seat == null && colIndex != perSideSeats && colIndex !== 4 && (
                                <div className="h-6 w-6 md:h-8 md:w-8"></div>
                              )}
                              {seat != null && (
                                <SeatButton
                                  activeStatus={activeStatus}
                                  activeIndexes={activeIndexes}
                                  number={seat.seatNo}
                                  bgColor={
                                    activeStatus.includes(seat.seatNo)
                                      ? 'bg-[#49CA56] text-white'
                                      : seat.amount === 0
                                        ? 'bg-gray-200'
                                        : seat.amount > 0 && seat.amount <= 100
                                          ? 'bg-teal-300'
                                          : seat.amount > 100 &&
                                              seat.amount <= 1000
                                            ? 'bg-violet-400'
                                            : seat.amount > 1000
                                              ? 'bg-rose-300'
                                              : seat.isBooked
                                                ? 'bg-transparent'
                                                : 'bg-gray-200'
                                  }
                                  seatData={seat}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
                {seatGrid && seatGrid?.length && (
                  <div className="-my-3 flex flex-col justify-between text-[10px] text-gray-500 md:-my-5 md:text-xs">
                    <span>EXIT</span>
                    <span>EXIT</span>
                  </div>
                )}
              </div>
              {seatGrid && seatGrid?.length && (
                <Image
                  src={wingBottom}
                  width={197}
                  height={200}
                  alt="Wing bottom"
                  className="h-auto w-[120px] object-contain md:w-[150px] lg:w-[197px]"
                />
              )}
            </div>
            {seatGrid && seatGrid?.length && (
              <Image
                src={flyghtback}
                alt="Flight back"
                className="h-auto w-[250px] object-contain md:mt-0 md:w-[260px] lg:w-[308px]"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SeatButton({
  number,
  bgColor,
  seatData,
  activeIndexes,
  activeStatus,
}: {
  number: number | string;
  bgColor: string;
  seatData: any;
  activeIndexes: any;
  activeStatus: string[];
}) {
  const selectedSSR = useSelector(
    (state: any) => state.flightSearch.selectedSSR,
  );
  const dispatch = useDispatch();

  if (number === '') {
    return null;
  }

  const handleSeatClick = (seatData: any) => {
    if (!seatData.isBooked) {
      const ssrData = JSON.parse(JSON.stringify(selectedSSR));
      if (activeStatus.includes(seatData.seatNo)) {
        // Deselect: clear the seat
        ssrData[activeIndexes.paxIndex][activeIndexes.i][activeIndexes.index][
          'seat'
        ] = {};
      } else {
        // Select: set the seat with active flag and all data
        ssrData[activeIndexes.paxIndex][activeIndexes.i][activeIndexes.index][
          'seat'
        ] = {
          ...seatData,
          active: true,
        };
      }
      dispatch(updateSelectedSSR(ssrData));
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`flex h-8 w-8 items-center justify-center rounded active:scale-105 ${
              seatData.isBooked
                ? 'cursor-not-allowed bg-transparent'
                : 'cursor-pointer ' + bgColor
            }`}
            onClick={() => handleSeatClick(seatData)}
          >
            {activeStatus.length > 0 &&
            activeStatus.includes(seatData.seatNo) ? (
              <Check className="h-4 w-4" />
            ) : (
              seatData.seatNo
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent className="bg-white">
          <div className="bg-tertiary-light">
            <div>{seatData.seatNo}</div>
            <div>{formatCurrency(seatData.amount)}</div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default Seat;
